import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

interface Calculation {
  id: number;
  type: string;
  quantity: string;
  price: string;
  unit: string;
  total: number;
}

const FarmCalculator = () => {
  const [calculations, setCalculations] = useState<Calculation[]>([]);
  const [currentCalculation, setCurrentCalculation] = useState({
    type: '',
    quantity: '',
    price: '',
    unit: '',
  });

  const handleAddCalculation = () => {
    if (currentCalculation.type && currentCalculation.quantity && currentCalculation.price) {
      setCalculations([
        ...calculations,
        {
          id: Date.now(),
          ...currentCalculation,
          total: parseFloat(currentCalculation.quantity) * parseFloat(currentCalculation.price),
        },
      ]);
      setCurrentCalculation({
        type: '',
        quantity: '',
        price: '',
        unit: '',
      });
    }
  };

  const handleDeleteCalculation = (id: number) => {
    setCalculations(calculations.filter((calc) => calc.id !== id));
  };

  const calculateTotal = () => {
    return calculations.reduce((total, calc) => total + calc.total, 0);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Farm Calculator
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Type"
              value={currentCalculation.type}
              onChange={(e) =>
                setCurrentCalculation({ ...currentCalculation, type: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={currentCalculation.quantity}
              onChange={(e) =>
                setCurrentCalculation({ ...currentCalculation, quantity: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Price"
              type="number"
              value={currentCalculation.price}
              onChange={(e) =>
                setCurrentCalculation({ ...currentCalculation, price: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Unit"
              value={currentCalculation.unit}
              onChange={(e) =>
                setCurrentCalculation({ ...currentCalculation, unit: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddCalculation}
              sx={{ height: '100%' }}
            >
              <AddIcon />
            </Button>
          </Grid>
        </Grid>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {calculations.map((calc) => (
                <TableRow key={calc.id}>
                  <TableCell>{calc.type}</TableCell>
                  <TableCell>{calc.quantity}</TableCell>
                  <TableCell>{calc.unit}</TableCell>
                  <TableCell>${calc.price}</TableCell>
                  <TableCell>${calc.total.toFixed(2)}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteCalculation(calc.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Typography variant="h6">
            Total: ${calculateTotal().toFixed(2)}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default FarmCalculator; 